import React, { useMemo, useCallback, useEffect, useLayoutEffect, useState, useRef, useContext } from 'react';
import axios from 'axios';
import {
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  useNodesState,
  useEdgesState,
  useStore,
  addEdge,
  Background,
  Controls,
  getBezierPath,
  BaseEdge,
  EdgeLabelRenderer,
  MarkerType,
  Position,
  Handle,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { annotateParallelEdges } from '../utils/parallelEdgeLayout';
import {
  dedupeLinkagesByCaseNode,
  linkageCaseNodeRowKey,
  uniqueLinkageCaseNodeCount,
} from '../utils/crossCaseLinkage';
import CrossCaseLinkageRecord from './CrossCaseLinkageRecord';
import {
  PdsBox,
  PdsCard,
  PdsMantineText,
  PdsNetworkIcon,
  PdsWriteEditArticleIcon,
} from '@podium-design-system/react-components';

/** Aligns with Case page metadata / secondary labels (textline 300) */
const META = { fontSize: '12px', lineHeight: 1.45 };
const NODE_TITLE = { fontSize: '12px', fontWeight: 600, lineHeight: 1.35 };
const EDGE_LABEL = { fontSize: '12px', fontWeight: 500, lineHeight: 1.2 };

/**
 * Long domains must wrap — layout assumes this outer width so child nodes are placed past the parent box.
 * Keep in sync with horizontal spacing below.
 */
const INV_NODE_MAX_WIDTH_PX = 300;
/** Horizontal gap between the right edge of a parent node and the left edge of its child (edge label corridor). */
const INV_EDGE_LABEL_CORRIDOR_PX = 140;
/** Minimum distance between node origin X positions at successive tree depths (max node width + corridor). */
const INV_HORIZONTAL_DEPTH_STEP_PX = INV_NODE_MAX_WIDTH_PX + INV_EDGE_LABEL_CORRIDOR_PX;
/** Vertical gap between sibling subtree lanes — tall wrapped titles + clearance for stacked edges. */
const INV_VERTICAL_LANE_GAP_PX = 220;
/** Approx. half-height of investigation node card (padding + title + status) for overlap checks. */
const INV_NODE_EST_HALF_HEIGHT_PX = 76;
/** Default Bezier curvature — keep in sync with getBezierPath defaults. */
const INV_EDGE_BEZIER_CURVATURE = 0.25;
/**

 * Lets custom edges commit drag offsets (merged into edge data by InvestigationNetworkFlowCanvas).
 */
const EdgeLabelDragContext = React.createContext(null);

/** Mirrors @xyflow xyflow/packages/system/src/utils/edges/bezier-edge.ts */
function calculateControlOffset(distance, curvature) {
  if (distance >= 0) {
    return 0.5 * distance;
  }
  return curvature * 25 * Math.sqrt(-distance);
}

function getControlWithCurvature(pos, x1, y1, x2, y2, c) {
  switch (pos) {
    case Position.Left:
      return [x1 - calculateControlOffset(x1 - x2, c), y1];
    case Position.Right:
      return [x1 + calculateControlOffset(x2 - x1, c), y1];
    case Position.Top:
      return [x1, y1 - calculateControlOffset(y1 - y2, c)];
    case Position.Bottom:
      return [x1, y1 + calculateControlOffset(y2 - y1, c)];
    default:
      return [x1, y1];
  }
}

function cubicBezierPoint(p0, p1, p2, p3, t) {
  const mt = 1 - t;
  const a = mt * mt * mt;
  const b = 3 * mt * mt * t;
  const c = 3 * mt * t * t;
  const d = t * t * t;
  return {
    x: a * p0.x + b * p1.x + c * p2.x + d * p3.x,
    y: a * p0.y + b * p1.y + c * p2.y + d * p3.y,
  };
}

function cubicBezierTangent(p0, p1, p2, p3, t) {
  const mt = 1 - t;
  const dx =
    3 * mt * mt * (p1.x - p0.x) + 6 * mt * t * (p2.x - p1.x) + 3 * t * t * (p3.x - p2.x);
  const dy =
    3 * mt * mt * (p1.y - p0.y) + 6 * mt * t * (p2.y - p1.y) + 3 * t * t * (p3.y - p2.y);
  const len = Math.hypot(dx, dy) || 1;
  return { dx: dx / len, dy: dy / len };
}

function estimateLabelPillSize(labelText) {
  const charW = 7.1;
  const padX = 16;
  const w = Math.min(280, Math.max(72, String(labelText).length * charW + padX));
  const h = 26;
  return { w, h };
}

function expandedNodeScreenRect(node) {
  const x = node.positionAbsolute?.x ?? node.position?.x ?? 0;
  const y = node.positionAbsolute?.y ?? node.position?.y ?? 0;
  const w = node.width ?? INV_NODE_MAX_WIDTH_PX;
  const h = node.height ?? INV_NODE_EST_HALF_HEIGHT_PX * 2;
  const pad = 4;
  return { left: x - pad, top: y - pad, right: x + w + pad, bottom: y + h + pad };
}

function pillOverlapsRect(cx, cy, pw, ph, rect) {
  const halfW = pw / 2;
  const halfH = ph / 2;
  const left = cx - halfW;
  const top = cy - halfH;
  const right = cx + halfW;
  const bottom = cy + halfH;
  return !(right < rect.left || left > rect.right || bottom < rect.top || top > rect.bottom);
}

function pillOverlapsAnyInvNode(cx, cy, pw, ph, graphNodes) {
  for (let i = 0; i < graphNodes.length; i += 1) {
    const n = graphNodes[i];
    const r = expandedNodeScreenRect(n);
    if (pillOverlapsRect(cx, cy, pw, ph, r)) return true;
  }
  return false;
}

/**
 * Places the label on the Bezier (samples t + small normal offsets). Prefers getBezierPath's t≈0.5 point first.
 */
function pickEdgeLabelFlowPosition({
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  graphNodes,
  pillW,
  pillH,
  libraryLabelX,
  libraryLabelY,
}) {
  const c = INV_EDGE_BEZIER_CURVATURE;
  const [sx1, sy1] = getControlWithCurvature(sourcePosition, sourceX, sourceY, targetX, targetY, c);
  const [tx2, ty2] = getControlWithCurvature(targetPosition, targetX, targetY, sourceX, sourceY, c);
  const p0 = { x: sourceX, y: sourceY };
  const p1 = { x: sx1, y: sy1 };
  const p2 = { x: tx2, y: ty2 };
  const p3 = { x: targetX, y: targetY };

  const ts = [0.5, 0.48, 0.52, 0.45, 0.55, 0.42, 0.58, 0.4, 0.6, 0.38, 0.62, 0.35, 0.65, 0.32, 0.68];
  const normalOffsets = [0, 10, -10, 18, -18, 26, -26, 34, -34];

  const tryCandidate = (cx, cy) =>
    !pillOverlapsAnyInvNode(cx, cy, pillW, pillH, graphNodes) ? { x: cx, y: cy } : null;

  const midFromLib = tryCandidate(libraryLabelX, libraryLabelY);
  if (midFromLib) return midFromLib;

  for (let ti = 0; ti < ts.length; ti += 1) {
    const t = ts[ti];
    const pt = cubicBezierPoint(p0, p1, p2, p3, t);
    const tan = cubicBezierTangent(p0, p1, p2, p3, t);
    const nx = -tan.dy;
    const ny = tan.dx;
    for (let oi = 0; oi < normalOffsets.length; oi += 1) {
      const off = normalOffsets[oi];
      const cx = pt.x + nx * off;
      const cy = pt.y + ny * off;
      const hit = tryCandidate(cx, cy);
      if (hit) return hit;
    }
  }

  return { x: libraryLabelX, y: libraryLabelY };
}

/**
 * JDBC/native SQL may return derive_from with casing that does not match frontend switch cases.
 */
function normalizeDeriveFromKey(raw) {
  if (raw == null || raw === '') return '';
  const s = String(raw).trim();
  if (!s) return '';
  return s.replace(/\s+/g, '_').toUpperCase();
}

/** React Flow v11 expects string node/edge ids; compare parents/children with normalized strings. */
function investigationId(invOrId) {
  if (invOrId == null || invOrId === '') return '';
  return String(invOrId);
}

/** Legacy stored connection_label / graph link text from before display-name fixes. */
function sanitizeExplicitEdgeLabel(text) {
  if (text == null || text === '') return text;
  const t = String(text).trim();
  const lower = t.toLowerCase();
  const fixes = {
    'publicwww website': 'PublicWWW Search',
    'domain on ip': 'Domain on IP',
    'ip address': 'IP Address',
    'mx record': 'MX Record',
  };
  return fixes[lower] ?? t;
}

const InvestigationNode = ({ data, selected }) => {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED': return '#22c55e';
      case 'RUNNING': return '#3b82f6';
      case 'FAILED': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'COMPLETED': return '✓';
      case 'RUNNING': return '⟳';
      case 'FAILED': return '✗';
      default: return '○';
    }
  };

  const scrollToInvestigationDetails = () => {
    setTimeout(() => {
      const investigationDetailsElement = document.querySelector('[data-investigation-details]');
      if (investigationDetailsElement) {
        const elementRect = investigationDetailsElement.getBoundingClientRect();
        const elementTop = elementRect.top + window.pageYOffset;
        const desiredScrollTop = Math.max(0, elementTop - 80);
        window.scrollTo({
          top: desiredScrollTop,
          behavior: 'smooth',
        });
      } else {
        window.scrollTo({
          top: window.scrollY + 400,
          behavior: 'smooth',
        });
      }
    }, 100);
  };

  const handleNodeClick = () => {
    if (confirmDelete) return; // don't select while confirming delete
    data.onNodeClick?.(data.id);
    scrollToInvestigationDetails();
  };

  const handleFindingsBadgeClick = (e) => {
    e.stopPropagation();
    data.onFindingsBadgeClick?.(data.id);
    data.onNodeClick?.(data.id);
    scrollToInvestigationDetails();
  };

  const handleDeleteConfirm = async (e) => {
    e.stopPropagation();
    setDeleting(true);
    try {
      await axios.delete(`/api/investigations/${data.id}`);
      data.onDeleteInvestigation?.(data.id);
    } catch (err) {
      console.error('Failed to delete investigation:', err);
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const statusBg = getStatusColor(data.status);

  return (
    <div
      style={{
        background: statusBg,
        color: 'white',
        border: selected ? '2px solid #2563eb' : '1px solid rgba(255, 255, 255, 0.6)',
        borderRadius: '12px',
        padding: '12px',
        minWidth: '140px',
        maxWidth: INV_NODE_MAX_WIDTH_PX,
        boxSizing: 'border-box',
        textAlign: 'center',
        boxShadow: selected
          ? '0 0 0 3px rgba(255, 255, 255, 0.95), 0 0 0 6px rgba(37, 99, 235, 0.85), 0 6px 16px rgba(0, 0, 0, 0.14)'
          : '0 2px 8px rgba(0, 0, 0, 0.1)',
        cursor: 'pointer',
        transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        position: 'relative',
      }}
      onClick={handleNodeClick}
    >
      {/* Cross-case link indicator */}
      {data.linkCount > 0 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            data.onLinkIconClick?.(data.id);
          }}
          title={`Linked to ${data.linkCount} case-node link${data.linkCount === 1 ? '' : 's'}`}
          style={{
            position: 'absolute',
            top: -8,
            right: -8,
            width: 24,
            height: 24,
            borderRadius: 12,
            border: '1px solid rgba(0,0,0,0.1)',
            backgroundColor: '#ffffff',
            color: '#0d9488',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
            cursor: 'pointer'
          }}
        >
          <span className="fas fa-link" style={{ fontSize: 12 }}></span>
          {data.linkCount > 0 && (
            <span style={{
              position: 'absolute',
              bottom: -6,
              right: -6,
              backgroundColor: '#0d9488',
              color: 'white',
              borderRadius: 8,
              fontSize: 9,
              padding: '0 4px',
              lineHeight: '14px',
              border: '1px solid white'
            }}>{data.linkCount}</span>
          )}
        </button>
      )}
      {/* Manual findings count — bottom-right; avoids top-right cross-case link control */}
      {(data.findingsCount ?? 0) > 0 && (
        <button
          type="button"
          title={`${data.findingsCount} finding${data.findingsCount === 1 ? '' : 's'} — open Findings`}
          aria-label={`Open Findings tab (${data.findingsCount} finding${data.findingsCount === 1 ? '' : 's'})`}
          onClick={handleFindingsBadgeClick}
          style={{
            position: 'absolute',
            bottom: -8,
            right: -8,
            minWidth: 22,
            height: 22,
            borderRadius: 11,
            padding: '0 5px',
            backgroundColor: '#0a0a0a',
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 3,
            boxShadow: '0 2px 6px rgba(0,0,0,0.28)',
            border: '1px solid rgba(255,255,255,0.22)',
            cursor: 'pointer',
            zIndex: 2,
          }}
        >
          <span style={{ display: 'flex', color: '#ffffff', lineHeight: 0 }}>
            <PdsWriteEditArticleIcon size="xs" />
          </span>
          <span style={{ fontSize: 10, fontWeight: 700, lineHeight: 1, fontFamily: 'inherit' }}>
            {data.findingsCount > 99 ? '99+' : data.findingsCount}
          </span>
        </button>
      )}
      {/* Delete button — only for sub-investigations */}
      {data.parentInvestigationId && (
        confirmDelete ? (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute', top: -10, left: -4, right: -4,
              background: '#1e293b', borderRadius: 8, padding: '4px 6px',
              display: 'flex', alignItems: 'center', gap: 4, zIndex: 10,
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            }}
          >
            <span style={{ fontSize: 10, color: '#f1f5f9', flex: 1, whiteSpace: 'nowrap' }}>
              {deleting ? 'Deleting…' : 'Delete?'}
            </span>
            {!deleting && (
              <>
                <button
                  onClick={handleDeleteConfirm}
                  style={{ fontSize: 10, padding: '2px 6px', background: '#ef4444', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                >
                  Yes
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setConfirmDelete(false); }}
                  style={{ fontSize: 10, padding: '2px 6px', background: '#64748b', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                >
                  No
                </button>
              </>
            )}
          </div>
        ) : (
          <button
            onClick={(e) => { e.stopPropagation(); setConfirmDelete(true); }}
            title="Delete this child node"
            style={{
              position: 'absolute', top: -8, left: -8,
              width: 20, height: 20, borderRadius: 10,
              border: '1px solid rgba(255,255,255,0.4)',
              backgroundColor: 'rgba(0,0,0,0.35)',
              color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', fontSize: 11, lineHeight: 1,
            }}
          >
            ×
          </button>
        )
      )}
      {/* Connection handles for horizontal flow */}
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: 'rgba(255, 255, 255, 0.8)', border: '2px solid #555' }}
      />
      <Handle
        type="source"
        position={Position.Right}
        style={{ background: 'rgba(255, 255, 255, 0.8)', border: '2px solid #555' }}
      />
      
      <div
        style={{
          ...NODE_TITLE,
          marginBottom: 6,
          wordBreak: 'break-word',
          fontFamily: 'inherit',
        }}
      >
        {data.targetValue}
      </div>
      <div
        style={{
          ...META,
          opacity: 0.95,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 4,
          fontFamily: 'inherit',
        }}
      >
        <span aria-hidden>{getStatusIcon(data.status)}</span>
        <span>{String(data.status || 'UNKNOWN').replace(/_/g, ' ')}</span>
      </div>
    </div>
  );
};

// Custom edge component with derive_from labels
const DeriveFromEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  markerEnd,
  style,
}) => {
  const laneOffset = Number(data?.parallelLaneOffset) || 0;
  const adjSourceY = sourceY + laneOffset;
  const adjTargetY = targetY + laneOffset;
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY: adjSourceY,
    sourcePosition,
    targetX,
    targetY: adjTargetY,
    targetPosition,
  });

  const rf = useReactFlow();
  const graphNodes = useStore((s) => s.getNodes());
  const dragCtx = useContext(EdgeLabelDragContext);
  const setEdgeLabelOffset = dragCtx?.setEdgeLabelOffset;

  const formatDeriveFrom = (deriveFrom) => {
    if (!deriveFrom) return '';
    return String(deriveFrom).toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  /**
   * Default edge text when investigations.connection_label is absent (legacy rows).
   * Only URL_SEARCH / URL_SCAN / SCREENSHOT get UrlScan product names; crt.sh CERTIFICATE/SANS keep title casing.
   */
  const formatConnectionLabel = (deriveFrom) => {
    const df = normalizeDeriveFromKey(deriveFrom);
    if (!df) return '';
    switch (df) {
      case 'URL_SEARCH':
        return 'URLScan Search';
      case 'URL_SCAN':
      case 'SCREENSHOT':
        return 'URLScan Scan';
      case 'PUBLICWWW_WEBSITE':
        return 'PublicWWW Search';
      case 'IP_ADDRESS':
        return 'IP Address';
      case 'DOMAIN_ON_IP':
        return 'Domain on IP';
      case 'MX_RECORD':
        return 'MX Record';
      case 'SUBDOMAIN':
        return 'Subdomain';
      case 'CERTIFICATE':
      case 'SANS':
      default:
        return formatDeriveFrom(deriveFrom);
    }
  };

  const getLabelColor = (deriveFrom, hasExplicitLabel) => {
    if (hasExplicitLabel) {
      return { bg: '#ecfeff', border: '#06b6d4', text: '#0e7490' };
    }
    const df = normalizeDeriveFromKey(deriveFrom);
    switch (df) {
      case 'CERTIFICATE': return { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' };
      case 'SANS': return { bg: '#dbeafe', border: '#3b82f6', text: '#1e40af' };
      case 'DOMAIN_ON_IP': return { bg: '#dcfce7', border: '#22c55e', text: '#166534' };
      case 'DOMAIN_ON_NS': return { bg: '#e0e7ff', border: '#6366f1', text: '#4338ca' };
      case 'MANUAL_FINDING':
      case 'OPERATING_COMPANY':
      case 'CONTACT_DETAILS':
      case 'NETWORK_TRAFFIC':
      case 'SOURCE_CODES':
      case 'LINKS':
      case 'LINK_DOMAINS':
      case 'RELATED_DOMAIN':
        return { bg: '#fce7f3', border: '#ec4899', text: '#be185d' };
      case 'URL_SCAN':
      case 'SCREENSHOT':
      case 'URL_SEARCH':
        return { bg: '#f3e8ff', border: '#a855f7', text: '#581c87' };
      case 'IP_ADDRESS': return { bg: '#fff7ed', border: '#f97316', text: '#9a3412' };
      case 'MX_RECORD': return { bg: '#ecfdf5', border: '#10b981', text: '#064e3b' };
      case 'SUBDOMAIN': return { bg: '#ccfbf1', border: '#14b8a6', text: '#0f766e' };
      case 'PUBLICWWW_WEBSITE': return { bg: '#fef9c3', border: '#eab308', text: '#713f12' };
      default: return { bg: 'rgba(255, 255, 255, 0.95)', border: 'rgba(100, 116, 139, 0.3)', text: '#374151' };
    }
  };

  const rawExplicitLabel =
    (data?.edgeLabel && String(data.edgeLabel))
    || (data?.connectionLabel && String(data.connectionLabel));
  const explicitLabel = rawExplicitLabel ? sanitizeExplicitEdgeLabel(rawExplicitLabel) : '';
  const labelText = explicitLabel || formatConnectionLabel(data?.deriveFrom);
  const labelColors = getLabelColor(data?.deriveFrom, Boolean(explicitLabel));
  const showLabel = Boolean(labelText);
  const edgeStyle = {
    ...style,
    ...(data?.isSupplementalEdge ? { strokeDasharray: '8 5' } : {}),
  };

  const { w: pillW, h: pillH } = estimateLabelPillSize(labelText);

  const autoPos = useMemo(
    () =>
      pickEdgeLabelFlowPosition({
        sourceX,
        sourceY: adjSourceY,
        targetX,
        targetY: adjTargetY,
        sourcePosition,
        targetPosition,
        graphNodes,
        pillW,
        pillH,
        libraryLabelX: labelX,
        libraryLabelY: labelY,
      }),
    [
      sourceX,
      adjSourceY,
      targetX,
      adjTargetY,
      sourcePosition,
      targetPosition,
      graphNodes,
      pillW,
      pillH,
      labelX,
      labelY,
    ],
  );

  const ox = Number(data?.labelOffset?.dx) || 0;
  const oy = Number(data?.labelOffset?.dy) || 0;
  const adjLabelX = autoPos.x + ox;
  const adjLabelY = autoPos.y + oy;

  const dragStartFlowRef = useRef({ x: 0, y: 0 });
  const offsetAtDragStartRef = useRef({ dx: 0, dy: 0 });
  const [dragging, setDragging] = useState(false);

  const onLabelPointerDown = useCallback(
    (e) => {
      if (!setEdgeLabelOffset || e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      setDragging(true);
      dragStartFlowRef.current = rf.screenToFlowPosition({ x: e.clientX, y: e.clientY });
      offsetAtDragStartRef.current = { dx: ox, dy: oy };

      const move = (ev) => {
        const cur = rf.screenToFlowPosition({ x: ev.clientX, y: ev.clientY });
        const dx = offsetAtDragStartRef.current.dx + (cur.x - dragStartFlowRef.current.x);
        const dy = offsetAtDragStartRef.current.dy + (cur.y - dragStartFlowRef.current.y);
        setEdgeLabelOffset(id, { dx, dy });
      };
      const up = () => {
        setDragging(false);
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        window.removeEventListener('pointercancel', up);
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
      window.addEventListener('pointercancel', up);
    },
    [setEdgeLabelOffset, rf, id, ox, oy],
  );

  return (
    <>
      <BaseEdge id={id} path={edgePath} markerEnd={markerEnd} style={edgeStyle} />
      {showLabel && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${adjLabelX}px,${adjLabelY}px)`,
              pointerEvents: 'all',
              cursor: setEdgeLabelOffset ? (dragging ? 'grabbing' : 'grab') : 'default',
              touchAction: 'none',
            }}
            className="nodrag nopan"
            title={setEdgeLabelOffset ? 'Drag to reposition this label' : undefined}
            onPointerDown={onLabelPointerDown}
          >
            <div
              style={{
                backgroundColor: labelColors.bg,
                border: `1px solid ${labelColors.border}`,
                borderRadius: 6,
                padding: '4px 8px',
                ...EDGE_LABEL,
                color: labelColors.text,
                whiteSpace: 'nowrap',
                boxShadow: dragging
                  ? '0 2px 8px rgba(0, 0, 0, 0.12)'
                  : '0 1px 2px rgba(0, 0, 0, 0.06)',
                fontFamily: 'inherit',
                userSelect: 'none',
              }}
            >
              {labelText}
            </div>
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

const nodeTypes = {
  investigation: InvestigationNode,
};

const edgeTypes = {
  deriveFrom: DeriveFromEdge,
};

/**
 * Fires a single smooth fitView pan to a specific node on first mount (e.g. arriving from home search panel).
 * Only runs once — when fitViewNodeId is provided and the node exists in the graph.
 */
function InvestigationFitViewToTarget({ fitViewNodeId, nodes, expectedNodeCount, onFitViewToTargetConsumed }) {
  const { fitView } = useReactFlow();
  const firedRef = useRef(false);

  useEffect(() => {
    firedRef.current = false;
  }, [fitViewNodeId]);

  useEffect(() => {
    if (!fitViewNodeId || firedRef.current) return;
    const nodeExists = nodes.some((n) => investigationId(n.id) === investigationId(fitViewNodeId));
    if (!nodeExists) return;
    firedRef.current = true;
    const raf = requestAnimationFrame(() => {
      fitView({
        nodes: [{ id: investigationId(fitViewNodeId) }],
        padding: 0.25,
        duration: 600,
        maxZoom: 1.25,
      });
      onFitViewToTargetConsumed?.();
    });
    return () => cancelAnimationFrame(raf);
  }, [fitViewNodeId, nodes, fitView, onFitViewToTargetConsumed]);

  // Target absent after RF has materialized the full investigation node list — unblock parent state.
  useEffect(() => {
    if (!fitViewNodeId || firedRef.current) return;
    const expected = Number(expectedNodeCount) || 0;
    if (expected <= 0 || nodes.length < expected) return;
    const nodeExists = nodes.some((n) => investigationId(n.id) === investigationId(fitViewNodeId));
    if (nodeExists) return;
    firedRef.current = true;
    onFitViewToTargetConsumed?.();
  }, [fitViewNodeId, nodes, expectedNodeCount, onFitViewToTargetConsumed]);

  return null;
}

/**
 * When investigation nodes are added, pans/zooms so the relevant node stays in view.
 *
 * - If the selected investigation is one of the newly added ids, frames that node (covers "just added + selected").
 * - If selection has not caught up yet (common after polling adds one child while selection stays on parent),
 *   waits briefly then frames the new node — optional second pass when selection lands on the new id.
 *
 * Does not advance the baseline id set until fit runs, so late selection updates still see "new" ids.
 *
 * committedInvestigationIdsRef lives outside the keyed ReactFlowProvider so provider remounts
 * (e.g. when supplemental graph links load) do not reset the baseline — avoids a second fitView
 * zoom onto the same selection.
 */
function InvestigationFitViewOnNewNode({
  caseId,
  investigations,
  selectedInvestigationId,
  nodes,
  committedInvestigationIdsRef,
  suppressNewNodeFit,
}) {
  const { setCenter, getNode, getViewport } = useReactFlow();
  const prevCaseIdRef = useRef(caseId);
  /** Ids introduced since committedInvestigationIdsRef that still need an add-fit */
  const pendingNewIdsRef = useRef([]);
  const deferTimerRef = useRef(null);
  /** Avoid resetting the defer timer when only React Flow node state refreshes for the same pending batch */
  const deferTimerSigRef = useRef('');
  const investigationsRef = useRef(investigations);
  const selectedRef = useRef(selectedInvestigationId);
  const nodesRef = useRef(nodes);

  investigationsRef.current = investigations;
  selectedRef.current = selectedInvestigationId;
  nodesRef.current = nodes;

  useEffect(() => {
    if (prevCaseIdRef.current !== caseId) {
      prevCaseIdRef.current = caseId;
      // Baseline ids are seeded by parent useLayoutEffect — do not clear committedInvestigationIdsRef here.
      pendingNewIdsRef.current = [];
      deferTimerSigRef.current = '';
      if (deferTimerRef.current != null) {
        window.clearTimeout(deferTimerRef.current);
        deferTimerRef.current = null;
      }
    }
  }, [caseId]);

  useEffect(() => {
    if (suppressNewNodeFit) return undefined;

    const currIds = (investigations || []).map((i) => investigationId(i.id)).filter(Boolean);
    const committed = committedInvestigationIdsRef.current;

    const brandNew = currIds.filter((id) => !committed.has(id));
    if (brandNew.length > 0) {
      const merged = new Set([...(pendingNewIdsRef.current || []), ...brandNew]);
      pendingNewIdsRef.current = [...merged].filter((id) => currIds.includes(id));
    }

    const pending = pendingNewIdsRef.current.filter((id) => currIds.includes(id));
    if (pending.length === 0) {
      committedInvestigationIdsRef.current = new Set(currIds);
      return undefined;
    }

    const sel = selectedInvestigationId ? investigationId(selectedInvestigationId) : '';
    const selectedIsPending = Boolean(sel && pending.includes(sel));

    let focusId = selectedIsPending ? sel : pending[pending.length - 1];

    const nodeReady = nodes.some((n) => investigationId(n.id) === focusId);
    if (!nodeReady) return undefined;

    const scheduleCenterOnNode = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const node = getNode(focusId);
          if (!node) return;
          const w =
            node.width ??
            node.measured?.width ??
            (typeof node.style?.width === 'number' ? node.style.width : INV_NODE_MAX_WIDTH_PX);
          const h = node.height ?? node.measured?.height ?? 80;
          const cx = node.position.x + Number(w) / 2;
          const cy = node.position.y + Number(h) / 2;
          const zoom = getViewport().zoom;
          setCenter(cx, cy, { zoom, duration: 500 });
          pendingNewIdsRef.current = [];
          committedInvestigationIdsRef.current = new Set(currIds);
          deferTimerSigRef.current = '';
        });
      });
    };

    const deferMs =
      pending.length === 1 && sel && !pending.includes(sel)
        ? 140
        : 0;

    if (deferMs === 0) {
      deferTimerSigRef.current = '';
      scheduleCenterOnNode();
      return undefined;
    }

    const deferSig = pending.slice().sort().join('|');
    if (deferTimerRef.current != null && deferTimerSigRef.current === deferSig) {
      return undefined;
    }
    deferTimerSigRef.current = deferSig;

    if (deferTimerRef.current != null) {
      window.clearTimeout(deferTimerRef.current);
      deferTimerRef.current = null;
    }

    deferTimerRef.current = window.setTimeout(() => {
      deferTimerRef.current = null;
      const latestInv = investigationsRef.current || [];
      const currIdsNow = latestInv.map((i) => investigationId(i.id)).filter(Boolean);
      const selLater = selectedRef.current ? investigationId(selectedRef.current) : '';
      const liveNodes = nodesRef.current || [];
      const pendingLater = pendingNewIdsRef.current.filter((id) => currIdsNow.includes(id));
      if (pendingLater.length === 0) {
        committedInvestigationIdsRef.current = new Set(currIdsNow);
        return;
      }
      let id = pendingLater.includes(selLater) ? selLater : pendingLater[pendingLater.length - 1];
      if (!liveNodes.some((n) => investigationId(n.id) === id)) return;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const node = getNode(id);
          if (!node) return;
          const w =
            node.width ??
            node.measured?.width ??
            (typeof node.style?.width === 'number' ? node.style.width : INV_NODE_MAX_WIDTH_PX);
          const h = node.height ?? node.measured?.height ?? 80;
          const cx = node.position.x + Number(w) / 2;
          const cy = node.position.y + Number(h) / 2;
          const zoom = getViewport().zoom;
          setCenter(cx, cy, { zoom, duration: 500 });
          pendingNewIdsRef.current = [];
          committedInvestigationIdsRef.current = new Set(currIdsNow);
          deferTimerSigRef.current = '';
        });
      });
    }, deferMs);

    return undefined;
  }, [
    investigations,
    selectedInvestigationId,
    nodes,
    suppressNewNodeFit,
    committedInvestigationIdsRef,
    getNode,
    getViewport,
    setCenter,
  ]);

  useEffect(
    () => () => {
      if (deferTimerRef.current != null) {
        window.clearTimeout(deferTimerRef.current);
        deferTimerRef.current = null;
      }
    },
    [],
  );

  return null;
}

/**
 * Hooks must sit under React Flow context; wrapped by a keyed provider so topology changes remount RF cleanly.
 */
function InvestigationNetworkFlowCanvas({
  caseId,
  investigations,
  selectedInvestigationId,
  fitViewNodeId,
  onFitViewToTargetConsumed,
  suppressNewNodeFit,
  committedInvestigationIdsRef,
  initialNodes,
  initialEdges,
}) {
  const edgeLabelOffsetsStorageKey = useMemo(
    () => `argus-inv-edge-label-off:${investigationId(caseId)}`,
    [caseId],
  );

  const [edgeLabelOffsets, setEdgeLabelOffsets] = useState(() => ({}));

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(edgeLabelOffsetsStorageKey);
      setEdgeLabelOffsets(raw ? JSON.parse(raw) : {});
    } catch {
      setEdgeLabelOffsets({});
    }
  }, [edgeLabelOffsetsStorageKey]);

  useEffect(() => {
    try {
      const trimmed = Object.fromEntries(
        Object.entries(edgeLabelOffsets).filter(
          ([, o]) => o && (Number(o.dx) !== 0 || Number(o.dy) !== 0),
        ),
      );
      if (Object.keys(trimmed).length === 0) {
        sessionStorage.removeItem(edgeLabelOffsetsStorageKey);
      } else {
        sessionStorage.setItem(edgeLabelOffsetsStorageKey, JSON.stringify(trimmed));
      }
    } catch (_) {
      /* ignore quota / private mode */
    }
  }, [edgeLabelOffsetsStorageKey, edgeLabelOffsets]);

  const setEdgeLabelOffset = useCallback((edgeId, offset) => {
    setEdgeLabelOffsets((prev) => ({
      ...prev,
      [edgeId]: { dx: offset.dx, dy: offset.dy },
    }));
  }, []);

  const edgeLabelDragContextValue = useMemo(
    () => ({ setEdgeLabelOffset }),
    [setEdgeLabelOffset],
  );

  const mergedInitialEdges = useMemo(
    () =>
      initialEdges.map((e) => ({
        ...e,
        data: {
          ...e.data,
          labelOffset: edgeLabelOffsets[e.id] ?? e.data?.labelOffset ?? { dx: 0, dy: 0 },
        },
      })),
    [initialEdges, edgeLabelOffsets],
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(mergedInitialEdges);

  useEffect(() => {
    setNodes((prev) => {
      const prevById = new Map((prev || []).map((n) => [investigationId(n.id), n]));
      return (initialNodes || []).map((n) => {
        const old = prevById.get(investigationId(n.id));
        if (!old) return n;
        return {
          ...n,
          position: n.position ?? old.position,
          width: old.width,
          height: old.height,
          measured: old.measured,
        };
      });
    });
  }, [initialNodes, setNodes]);

  useEffect(() => {
    setEdges(mergedInitialEdges);
  }, [mergedInitialEdges, setEdges]);

  const onInit = useCallback((instance) => {
    if (fitViewNodeId) return;
    const current = instance.getNodes();
    if (!current.length) return;
    requestAnimationFrame(() => {
      instance.fitView({ padding: 0.25, maxZoom: 1.15, duration: 0 });
    });
  }, [fitViewNodeId]);

  const updatedNodes = useMemo(() => {
    const sel = selectedInvestigationId ? investigationId(selectedInvestigationId) : '';
    return nodes.map((node) => ({
      ...node,
      selected: investigationId(node.id) === sel,
    }));
  }, [nodes, selectedInvestigationId]);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges],
  );

  return (
    <EdgeLabelDragContext.Provider value={edgeLabelDragContextValue}>
      <ReactFlow
      nodes={updatedNodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onConnect={onConnect}
      onInit={onInit}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
      attributionPosition="bottom-left"
      nodesDraggable={false}
      nodesConnectable={false}
      elementsSelectable={true}
      panOnDrag={true}
      panOnScroll={false}
      zoomOnScroll={false}
      zoomOnPinch={true}
      zoomOnDoubleClick={false}
      preventScrolling={false}
      minZoom={0.25}
      maxZoom={2}
    >
      <InvestigationFitViewOnNewNode
        caseId={caseId}
        investigations={investigations}
        selectedInvestigationId={selectedInvestigationId}
        nodes={nodes}
        committedInvestigationIdsRef={committedInvestigationIdsRef}
        suppressNewNodeFit={suppressNewNodeFit}
      />
      <InvestigationFitViewToTarget
        fitViewNodeId={fitViewNodeId}
        nodes={nodes}
        expectedNodeCount={(investigations || []).length}
        onFitViewToTargetConsumed={onFitViewToTargetConsumed}
      />
      <Background color="#f6f8fa" />
      <Controls showInteractive={true} />
    </ReactFlow>
    </EdgeLabelDragContext.Provider>
  );
}

const InvestigationNetworkFlow = ({
  caseId,
  investigations,
  onNodeClick,
  onDeleteInvestigation,
  selectedInvestigationId,
  onFindingsBadgeClick,
  fitViewNodeId,
  onFitViewToTargetConsumed,
  linkageRefreshNonce = 0,
}) => {
  /** Survives ReactFlowProvider remounts — see InvestigationFitViewOnNewNode */
  const committedInvestigationIdsRef = useRef(new Set());
  const committedBaselineCaseRef = useRef(null);
  const baselineNeedsSeedRef = useRef(false);

  useLayoutEffect(() => {
    if (!caseId) return;
    if (committedBaselineCaseRef.current !== caseId) {
      committedBaselineCaseRef.current = caseId;
      committedInvestigationIdsRef.current = new Set();
      baselineNeedsSeedRef.current = true;
    }
  }, [caseId]);

  useLayoutEffect(() => {
    if (!caseId || !baselineNeedsSeedRef.current) return;
    const ids = (investigations || []).map((i) => investigationId(i.id)).filter(Boolean);
    if (ids.length === 0) return;
    committedInvestigationIdsRef.current = new Set(ids);
    baselineNeedsSeedRef.current = false;
  }, [caseId, investigations]);

  const suppressNewNodeFit = Boolean(fitViewNodeId);

  const [linksByInvestigation, setLinksByInvestigation] = useState({});
  const [graphLinks, setGraphLinks] = useState([]);
  const [graphLinksError, setGraphLinksError] = useState(null);
  const [linksBatchError, setLinksBatchError] = useState(null);
  const [linksModalOpen, setLinksModalOpen] = useState(false);
  const [linksModalData, setLinksModalData] = useState({ investigationId: null, links: [] });

  const investigationIdsKey = useMemo(
    () => [...new Set((investigations || []).map((i) => investigationId(i.id)).filter(Boolean))].sort().join('|'),
    [investigations],
  );

  const reloadGraphLinks = useCallback(() => {
    if (!caseId) {
      setGraphLinks([]);
      setGraphLinksError(null);
      return;
    }
    setGraphLinksError(null);
    axios
      .get(`/api/investigations/case/${caseId}/graph-links`)
      .then((res) => setGraphLinks(Array.isArray(res.data) ? res.data : []))
      .catch((e) => {
        setGraphLinksError(
          e?.response?.data?.message || e?.message || 'Could not load supplemental graph links.'
        );
      });
  }, [caseId]);

  useEffect(() => {
    reloadGraphLinks();
  }, [reloadGraphLinks, investigationIdsKey]);

  const reloadCrossCaseLinkages = useCallback(() => {
    if (!caseId || !(investigations || []).length) {
      setLinksByInvestigation({});
      setLinksBatchError(null);
      return;
    }
    setLinksBatchError(null);
    axios
      .get(`/api/investigations/case/${caseId}/cross-case-linkages`)
      .then((res) => {
        const batch = res.data && typeof res.data === 'object' ? res.data : {};
        const map = {};
        (investigations || []).forEach((inv) => {
          const id = investigationId(inv.id);
          map[id] = batch[id] ?? batch[inv.id] ?? [];
        });
        setLinksByInvestigation(map);
      })
      .catch((e) => {
        setLinksByInvestigation({});
        setLinksBatchError(
          e?.response?.data?.message || e?.message || 'Could not load cross-case linkages.'
        );
      });
  }, [caseId, investigations]);

  useEffect(() => {
    reloadCrossCaseLinkages();
  }, [reloadCrossCaseLinkages]);

  useEffect(() => {
    if (!linkageRefreshNonce) return;
    reloadCrossCaseLinkages();
  }, [linkageRefreshNonce, reloadCrossCaseLinkages]);

  // Convert investigations to React Flow format
  const initialNodes = useMemo(() => {
    if (!investigations || investigations.length === 0) {
      return [];
    }

    // Create horizontal layout - arrange nodes from left to right
    const nodeMap = new Map();
    const roots = [];
    
    // First pass: create all nodes and identify roots
    investigations.forEach((inv, index) => {
      const idStr = investigationId(inv.id);
      const node = {
        id: idStr,
        type: 'investigation',
        position: { x: 0, y: 0 }, // Will be calculated later
        data: {
          ...inv,
          onNodeClick,
          onDeleteInvestigation,
          onFindingsBadgeClick,
          linkCount: uniqueLinkageCaseNodeCount(
            linksByInvestigation[idStr] || linksByInvestigation[inv.id] || [],
          ),
          onLinkIconClick: (invIdArg) => {
            const key = investigationId(invIdArg);
            const raw = linksByInvestigation[key] || linksByInvestigation[invIdArg] || [];
            setLinksModalData({
              investigationId: key,
              links: dedupeLinkagesByCaseNode(raw),
            });
            setLinksModalOpen(true);
          },
        },
        draggable: false, // Lock node position
      };
      nodeMap.set(idStr, node);
      
      if (!inv.parentInvestigationId) {
        roots.push(node);
      }
    });

    // Horizontal layout: each depth step moves right by at least max node width + label corridor (no overlap).
    const horizontalSpacing = INV_HORIZONTAL_DEPTH_STEP_PX;
    const verticalSpacing = INV_VERTICAL_LANE_GAP_PX;
    
    // Build tree structure and calculate levels
    const buildTree = (nodeId, level = 0) => {
      const node = nodeMap.get(nodeId);
      if (!node) {
        return { node: null, level, children: [], height: 1 };
      }

      const children = investigations.filter(
        (inv) =>
          inv.parentInvestigationId != null &&
          investigationId(inv.parentInvestigationId) === investigationId(nodeId)
      );
      const childTrees = children.map((child) => buildTree(investigationId(child.id), level + 1));
      
      return {
        node,
        level,
        children: childTrees,
        height: Math.max(1, childTrees.reduce((sum, child) => sum + child.height, 0))
      };
    };

    // Position nodes horizontally
    let currentY = 100;
    roots.forEach(root => {
      const tree = buildTree(investigationId(root.id));
      
      const positionTree = (treeNode, x, startY) => {
        if (!treeNode?.node) return;
        treeNode.node.position = { x, y: startY };
        
        let childY = startY;
        treeNode.children.forEach(child => {
          positionTree(child, x + horizontalSpacing, childY);
          childY += child.height * verticalSpacing;
        });
      };
      
      positionTree(tree, 50, currentY);
      currentY += tree.height * verticalSpacing + 130;
    });

    const nodesArray = Array.from(nodeMap.values());

    return nodesArray;
  }, [investigations, onNodeClick, onDeleteInvestigation, onFindingsBadgeClick, linksByInvestigation]);

  const initialEdges = useMemo(() => {
    if (!investigations || investigations.length === 0) {
      return [];
    }

    const baseEdges = investigations
      .filter(inv => inv.parentInvestigationId)
      .map(inv => ({
        id: `edge-${investigationId(inv.parentInvestigationId)}-${investigationId(inv.id)}`,
        source: investigationId(inv.parentInvestigationId),
        target: investigationId(inv.id),
        type: 'deriveFrom',
        data: {
          deriveFrom: inv.deriveFrom,
          connectionLabel: inv.connectionLabel,
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 20,
          height: 20,
          color: 'rgba(100, 116, 139, 0.6)',
        },
        style: {
          strokeWidth: 2,
          stroke: 'rgba(100, 116, 139, 0.4)',
        },
      }));

    const extraEdges = (graphLinks || []).map((gl, idx) => ({
      id: `edge-graph-${investigationId(gl.sourceInvestigationId)}-${investigationId(gl.targetInvestigationId)}-${idx}`,
      source: investigationId(gl.sourceInvestigationId),
      target: investigationId(gl.targetInvestigationId),
      type: 'deriveFrom',
      data: {
        edgeLabel: gl.label || 'Common link identified',
        isSupplementalEdge: true,
      },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 20,
        height: 20,
        color: 'rgba(6, 182, 212, 0.75)',
      },
      style: {
        strokeWidth: 2,
        stroke: 'rgba(6, 182, 212, 0.55)',
      },
    }));

    return annotateParallelEdges([...baseEdges, ...extraEdges]);
  }, [investigations, graphLinks]);

  /** Remount React Flow when node ids or supplemental edges change — not on status-only poll updates. */
  const investigationNetworkMountKey = useMemo(() => {
    const linksKey = (graphLinks || [])
      .map((gl) =>
        `${investigationId(gl.sourceInvestigationId)}>${investigationId(gl.targetInvestigationId)}>${String(gl.label ?? '')}`,
      )
      .sort()
      .join('~');
    return `${investigationIdsKey}::${linksKey}`;
  }, [investigationIdsKey, graphLinks]);

  if (investigations.length === 0) {
    return null;
  }

  return (
    <PdsCard border borderRadius="md" padding="md" direction="column" gap="md" style={{ marginBottom: 16 }}>
      <PdsBox direction="row" alignItems="center" gap="xs">
        <PdsNetworkIcon color="neutral" size="sm" />
        <PdsMantineText type="textline" fontWeight="strong" color="neutral" component="h2" style={{ margin: 0 }}>
          Node Network ({investigations.length})
        </PdsMantineText>
      </PdsBox>

      {(graphLinksError || linksBatchError) && (
        <PdsBox direction="column" gap="xs">
          {graphLinksError && (
            <div
              role="alert"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 8,
                padding: '8px 12px',
                borderRadius: 8,
                border: '1px solid #fecaca',
                background: '#fef2f2',
                fontSize: 13,
                color: '#991b1b',
              }}
            >
              <span>Supplemental graph links could not be loaded: {graphLinksError}</span>
              <button type="button" className="btn btn-sm btn-secondary" onClick={() => reloadGraphLinks()}>
                Retry
              </button>
            </div>
          )}
          {linksBatchError && (
            <div
              role="alert"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 8,
                padding: '8px 12px',
                borderRadius: 8,
                border: '1px solid #fecaca',
                background: '#fef2f2',
                fontSize: 13,
                color: '#991b1b',
              }}
            >
              <span>Cross-case link badges could not be loaded: {linksBatchError}</span>
              <button type="button" className="btn btn-sm btn-secondary" onClick={() => reloadCrossCaseLinkages()}>
                Retry
              </button>
            </div>
          )}
        </PdsBox>
      )}

      <div className="inv-network-flow" style={{ height: 500, border: '1px solid #d0d7de', borderRadius: 8, overflow: 'hidden' }}>
        <ReactFlowProvider key={investigationNetworkMountKey}>
          <InvestigationNetworkFlowCanvas
            caseId={caseId}
            investigations={investigations}
            selectedInvestigationId={selectedInvestigationId}
            fitViewNodeId={fitViewNodeId}
            onFitViewToTargetConsumed={onFitViewToTargetConsumed}
            suppressNewNodeFit={suppressNewNodeFit}
            committedInvestigationIdsRef={committedInvestigationIdsRef}
            initialNodes={initialNodes}
            initialEdges={initialEdges}
          />
        </ReactFlowProvider>
      </div>

      {/* Cross-case Links Modal */}
      {linksModalOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}
          onClick={() => setLinksModalOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ backgroundColor: 'white', borderRadius: 8, width: '90%', maxWidth: 640, maxHeight: '80vh', overflow: 'auto', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottom: '1px solid #e5e7eb' }}>
              <h3 style={{ margin: 0, fontSize: 16 }}>Cross-case links</h3>
              <button onClick={() => setLinksModalOpen(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#64748b' }}>×</button>
            </div>
            <div style={{ padding: 16 }}>
              {linksModalData.links.length === 0 ? (
                <div style={{ color: '#64748b', fontSize: 14 }}>No links found.</div>
              ) : (
                <div style={{ display: 'grid', gap: 8 }}>
                  {linksModalData.links.map((link) => (
                    <CrossCaseLinkageRecord
                      key={linkageCaseNodeRowKey(link)}
                      link={link}
                      onNavigate={() => setLinksModalOpen(false)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </PdsCard>
  );
};

export default InvestigationNetworkFlow;
